import React from "react";

/**
 * PersonasContent — explains what Personas are in Stackform, how to manage
 * them, and describes the three platform-provided defaults.
 */
const PersonasContent = () => (
  <div style={{ lineHeight: 1.65 }}>
    <h2 style={{ marginTop: 0, marginBottom: "0.75rem" }}>Personas</h2>

    <p>
      A <strong>Persona</strong> is a saved system prompt that gives an agent a
      specific role, tone, or area of expertise. Once assigned, every response
      that agent produces will reflect that lens — without you having to
      re-explain it on every question.
    </p>

    <p>
      Stackform's three agents — <strong>Aeri</strong>, <strong>Capo</strong>,
      and <strong>Cardian</strong> — each accept an independent persona
      assignment, so you can configure them to approach the same question from
      complementary angles in a single run.
    </p>

    {/* ── Where to manage ─────────────────────────────────────────── */}
    <h3 style={{ marginBottom: "0.4rem" }}>Managing Personas</h3>
    <ol style={{ paddingLeft: "1.25rem", marginTop: 0 }}>
      <li>
        Open <strong>User Settings → Personas</strong> to create, edit, or
        reset personas.
      </li>
      <li>
        Assign a persona to each agent under{" "}
        <strong>User Settings → Model Defaults → Agent Models</strong>.
      </li>
      <li>
        The assignment takes effect immediately on the next question you send.
      </li>
    </ol>

    <p style={{ fontSize: "0.88rem", color: "var(--text-secondary, #888)" }}>
      Personas are stored per-user and persist across sessions. Clicking{" "}
      <em>Restore All Default Personas</em> in the Personas tab resets all
      three slots to the platform defaults below.
    </p>

    <hr style={{ margin: "1.25rem 0", borderColor: "var(--border-color, #eee)" }} />

    {/* ── Default personas ─────────────────────────────────────────── */}
    <h3 style={{ marginBottom: "0.75rem" }}>Platform Default Personas</h3>
    <p style={{ marginTop: 0 }}>
      Stackform ships with three pre-built personas designed for analytical
      depth and balanced debate. They work especially well in{" "}
      <strong>Swarm mode</strong>, where each agent takes a different
      perspective on the same question.
    </p>

    {/* Analyst */}
    <div style={{
      border: "1px solid var(--border-color, #ddd)",
      borderRadius: 8,
      padding: "0.85rem 1rem",
      marginBottom: "0.85rem",
      background: "var(--hover-color, rgba(0,0,0,0.03))",
    }}>
      <h4 style={{ margin: "0 0 0.35rem", color: "var(--primary-color, #636cff)" }}>
        Analyst
      </h4>
      <p style={{ margin: 0, fontSize: "0.88rem" }}>
        A rigorous, evidence-driven thinker. Examines every claim with
        precision — surfacing supporting data, quantifying uncertainty, and
        exposing gaps in reasoning. The Analyst aims for calibrated insight:
        neither optimistic nor pessimistic, just accurate. When evidence is
        incomplete, it says so explicitly and explains what additional
        information would resolve the gap.
      </p>
      <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "var(--text-secondary, #888)" }}>
        Best used for: research questions, fact-checking, data interpretation,
        any topic where rigour matters more than narrative.
      </p>
    </div>

    {/* Skeptic */}
    <div style={{
      border: "1px solid var(--border-color, #ddd)",
      borderRadius: 8,
      padding: "0.85rem 1rem",
      marginBottom: "0.85rem",
      background: "var(--hover-color, rgba(0,0,0,0.03))",
    }}>
      <h4 style={{ margin: "0 0 0.35rem", color: "var(--primary-color, #636cff)" }}>
        Skeptic
      </h4>
      <p style={{ margin: 0, fontSize: "0.88rem" }}>
        A sharp, constructive critic. Interrogates assumptions, identifies
        failure modes, and stress-tests every claim against realistic
        adversarial conditions — regardless of who made it. The Skeptic points
        out what the prevailing view overlooks, who bears hidden costs, and
        where confidence has outpaced evidence. Its dissent is pressure that
        separates robust ideas from fragile ones.
      </p>
      <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "var(--text-secondary, #888)" }}>
        Best used for: strategy review, risk analysis, evaluating proposals
        before committing to them, devil's-advocate reasoning.
      </p>
    </div>

    {/* Mediator */}
    <div style={{
      border: "1px solid var(--border-color, #ddd)",
      borderRadius: 8,
      padding: "0.85rem 1rem",
      marginBottom: "0.85rem",
      background: "var(--hover-color, rgba(0,0,0,0.03))",
    }}>
      <h4 style={{ margin: "0 0 0.35rem", color: "var(--primary-color, #636cff)" }}>
        Mediator
      </h4>
      <p style={{ margin: 0, fontSize: "0.88rem" }}>
        A balanced synthesizer. When multiple perspectives are present,
        the Mediator finds the signal in the tension: identifying where views
        align, where disagreement is substantive versus semantic, and what a
        defensible middle path looks like. It proposes concrete, actionable
        recommendations that honestly acknowledge remaining trade-offs.
      </p>
      <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "var(--text-secondary, #888)" }}>
        Best used for: summarising a Swarm debate, resolving conflicting
        recommendations, decision-making under uncertainty.
      </p>
    </div>

    <hr style={{ margin: "1.25rem 0", borderColor: "var(--border-color, #eee)" }} />

    {/* ── Swarm tip ─────────────────────────────────────────────────── */}
    <h3 style={{ marginBottom: "0.4rem" }}>Tip: Personas in Swarm Mode</h3>
    <p style={{ marginTop: 0 }}>
      Assigning each enabled agent its own default persona before
      starting a <strong>Swarm</strong> debate gives each a distinct
      analytical stance. The result is a structured multi-perspective
      exploration rather than agents that agree with each other —
      surfacing trade-offs you might otherwise miss. (Swarm needs at least two
      active agents; you can disable one by setting its provider to None in
      Settings → Model Defaults.)
    </p>

    <p style={{ marginTop: 0 }}>
      Personas also work in <strong>Ask</strong> and{" "}
      <strong>Workflow</strong> modes: each agent node in a workflow can carry
      its own persona assignment, letting you orchestrate specialised roles
      across a multi-step pipeline.
    </p>
  </div>
);

export default PersonasContent;
